/**
 * SystemStatusCard
 *
 * Visual config-status grid for the Admin Dashboard:
 *   - Resend (email service) configuration
 *   - MongoDB connection + environment + dataset stats
 *   - Sync status (last login / signup, active counts)
 *   - Stripe (payments) — placeholder badge with active sub count
 *
 * State for these checks is owned by AdminDashboard.jsx and passed down
 * as props — that page already fetches /api/admin/email-config,
 * /api/admin/database-status, /api/admin/sync-status as part of its
 * boot-up batch, so duplicating the fetches here would be wasteful.
 *
 * Extracted from AdminDashboard.jsx for maintainability. No behavior
 * changes vs. the inline JSX.
 */
import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Server, RefreshCw, CheckCircle2, XCircle, AlertCircle, Activity,
} from 'lucide-react';

export function SystemStatusCard({ systemStatus, databaseStatus, syncStatus, analytics, loading }) {
  return (
    <Card className="mb-8" data-testid="system-status-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Server className="h-5 w-5 text-[#00E5CC]" />
          System Status
        </CardTitle>
        <CardDescription>Email and service configuration</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Email Configuration Status */}
          <div data-testid="system-status-email" className={`p-4 rounded-lg border ${
            systemStatus?.resend_api_key_configured
              ? 'bg-green-100 dark:bg-green-950 border-green-300 dark:border-green-700'
              : 'bg-red-100 dark:bg-red-950 border-red-300 dark:border-red-700'
          }`}>
            <div className="flex items-center gap-2 mb-2">
              {systemStatus?.resend_api_key_configured ? (
                <CheckCircle2 className="h-5 w-5 text-green-700 dark:text-green-400" />
              ) : (
                <XCircle className="h-5 w-5 text-red-700 dark:text-red-400" />
              )}
              <span className="font-semibold text-gray-900 dark:text-white">Email Service (Resend)</span>
            </div>
            {systemStatus ? (
              <div className="text-sm space-y-1">
                <p className={`font-medium ${systemStatus.resend_api_key_configured ? 'text-green-800 dark:text-green-300' : 'text-red-800 dark:text-red-300'}`}>
                  API Key: {systemStatus.resend_api_key_configured ? 'Configured' : 'Missing'}
                </p>
                {systemStatus.resend_api_key_configured && (
                  <>
                    <p className="text-gray-700 dark:text-gray-200">
                      Key prefix: <code className="bg-white/50 dark:bg-gray-800 px-1 rounded font-mono text-xs">{systemStatus.resend_api_key_prefix}...</code>
                    </p>
                    <p className="text-gray-700 dark:text-gray-200 text-xs">
                      Sender: {systemStatus.sender_email_formatted}
                    </p>
                  </>
                )}
                {!systemStatus.resend_api_key_configured && (
                  <p className="text-red-700 dark:text-red-300 text-xs mt-2 font-medium">
                    {systemStatus.error ? `Error: ${systemStatus.error}` : 'Add RESEND_API_KEY to environment variables'}
                  </p>
                )}
              </div>
            ) : loading ? (
              <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 text-sm">
                <RefreshCw className="h-4 w-4 animate-spin" />
                Loading...
              </div>
            ) : (
              <p className="text-orange-600 dark:text-orange-400 text-sm">Could not load email config. Try refreshing.</p>
            )}
          </div>

          {/* Database Status */}
          <div data-testid="system-status-database" className={`p-4 rounded-lg border ${
            databaseStatus?.is_production
              ? 'bg-green-100 dark:bg-green-950 border-green-300 dark:border-green-700'
              : 'bg-amber-100 dark:bg-amber-950 border-amber-300 dark:border-amber-700'
          }`}>
            <div className="flex items-center gap-2 mb-2">
              {databaseStatus?.is_production ? (
                <CheckCircle2 className="h-5 w-5 text-green-700 dark:text-green-400" />
              ) : (
                <AlertCircle className="h-5 w-5 text-amber-700 dark:text-amber-400" />
              )}
              <span className="font-semibold text-gray-900 dark:text-white">Database</span>
              {databaseStatus && (
                <Badge className={`ml-auto text-xs ${
                  databaseStatus.is_production
                    ? 'bg-green-600 text-white'
                    : 'bg-amber-600 text-white'
                }`}>
                  {databaseStatus.environment?.toUpperCase() || 'UNKNOWN'}
                </Badge>
              )}
            </div>
            {databaseStatus ? (
              <div className="text-sm space-y-1">
                <p className={`font-medium ${databaseStatus.is_production ? 'text-green-800 dark:text-green-300' : 'text-amber-800 dark:text-amber-300'}`}>
                  {databaseStatus.database_type}: Connected
                </p>
                <p className="text-gray-700 dark:text-gray-200">
                  Users: {databaseStatus.stats?.users || '—'} | Phrases: {databaseStatus.stats?.phrases || '—'}
                </p>
                {databaseStatus.stats?.audio_coverage !== undefined && (
                  <p className="text-gray-700 dark:text-gray-200 text-xs">
                    Audio: {databaseStatus.stats.audio_coverage}% ({databaseStatus.stats.phrases_with_audio || 0} files)
                  </p>
                )}
                {!databaseStatus.is_production && (
                  <p className="text-amber-700 dark:text-amber-300 text-xs mt-2 font-medium">
                    Preview mode - data differs from production
                  </p>
                )}
              </div>
            ) : loading ? (
              <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 text-sm">
                <RefreshCw className="h-4 w-4 animate-spin" />
                Loading...
              </div>
            ) : (
              <p className="text-orange-600 dark:text-orange-400 text-sm">Could not load database status.</p>
            )}
          </div>

          {/* Sync Status */}
          <div data-testid="system-status-sync" className={`p-4 rounded-lg border ${
            syncStatus?.status === 'healthy'
              ? 'bg-emerald-100 dark:bg-emerald-950 border-emerald-300 dark:border-emerald-700'
              : syncStatus?.status === 'quiet'
              ? 'bg-yellow-100 dark:bg-yellow-950 border-yellow-300 dark:border-yellow-700'
              : 'bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-700'
          }`}>
            <div className="flex items-center gap-2 mb-2">
              <Activity className={`h-5 w-5 ${
                syncStatus?.status === 'healthy' ? 'text-emerald-700 dark:text-emerald-400' :
                syncStatus?.status === 'quiet' ? 'text-yellow-700 dark:text-yellow-400' :
                'text-gray-500'
              }`} />
              <span className="font-semibold text-gray-900 dark:text-white">Site Traffic</span>
              {syncStatus && (
                <Badge className={`ml-auto text-xs ${
                  syncStatus.status === 'healthy' ? 'bg-emerald-600 text-white' :
                  syncStatus.status === 'quiet' ? 'bg-yellow-600 text-white' :
                  'bg-gray-500 text-white'
                }`}>
                  {syncStatus.status?.toUpperCase() || 'UNKNOWN'}
                </Badge>
              )}
            </div>
            {syncStatus && !syncStatus.error ? (
              <div className="text-sm space-y-1">
                <p className="text-gray-700 dark:text-gray-200">
                  Last activity: <span className="font-medium">{syncStatus.latest_activity?.time_ago || syncStatus.latest_login?.time_ago || 'Never'}</span>
                </p>
                <p className="text-gray-700 dark:text-gray-200">
                  Last login: <span className="font-medium">{syncStatus.latest_login?.time_ago || 'Never'}</span>
                </p>
                <p className="text-gray-700 dark:text-gray-200">
                  Last signup: <span className="font-medium">{syncStatus.latest_registration?.time_ago || 'Never'}</span>
                </p>
                <div className="flex gap-3 mt-2 text-xs">
                  <span className="bg-white/50 dark:bg-gray-700 px-2 py-1 rounded">
                    {syncStatus.activity_summary?.active_users_24h || 0} active (24h)
                  </span>
                  <span className="bg-white/50 dark:bg-gray-700 px-2 py-1 rounded">
                    {syncStatus.activity_summary?.new_users_7d || 0} new (7d)
                  </span>
                </div>
              </div>
            ) : loading ? (
              <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 text-sm">
                <RefreshCw className="h-4 w-4 animate-spin" />
                Loading...
              </div>
            ) : (
              <p className="text-orange-600 dark:text-orange-400 text-sm">Could not load site traffic.</p>
            )}
          </div>

          {/* Stripe Status */}
          <div data-testid="system-status-stripe" className="p-4 rounded-lg border bg-blue-100 dark:bg-blue-950 border-blue-300 dark:border-blue-700">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="h-5 w-5 text-blue-700 dark:text-blue-400" />
              <span className="font-semibold text-gray-900 dark:text-white">Payments (Stripe)</span>
            </div>
            <div className="text-sm space-y-1">
              <p className="text-blue-800 dark:text-blue-300 font-medium">
                Paid Subs: {analytics?.active_subscribers || 0}
              </p>
              <p className="text-gray-700 dark:text-gray-200 text-xs">
                Check Stripe Dashboard for payment status
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default SystemStatusCard;
